/**
 * `Catalog`: indexed, read-only access to a catalogue snapshot (cards, printings, sets, aliases).
 *
 * Rules identity (research 03 §1.5): variant rows (Metal and Starter legends, Launch Exclusive, extra
 * token arts) are separate catalogue cards but the same card for the rules. `resolveAlias` folds them to
 * the base card; decks keep the variant's own printing for art.
 */
import type { CardId } from '@play/engine';
import { normaliseCardName } from './names';
import { plainestPrinting } from './printings';
import { type CatalogCard, type CatalogData, CatalogDataSchema, type CatalogPrinting, type CatalogSet } from './schema';
import { isUniqueText } from './text';

/** Indexed read-only access to one catalogue snapshot. */
export class Catalog {
  readonly version: string;
  readonly checksum: string;
  readonly createdAt: string;
  readonly data: CatalogData;
  private readonly cardsById = new Map<string, CatalogCard>();
  private readonly printingsById = new Map<string, CatalogPrinting>();
  private readonly printingsByCard = new Map<string, CatalogPrinting[]>();
  private readonly setsByCode = new Map<string, CatalogSet>();
  private readonly cardsByName = new Map<string, CatalogCard[]>();

  constructor(data: CatalogData) {
    this.data = data;
    this.version = data.version;
    this.checksum = data.checksum;
    this.createdAt = data.createdAt;
    for (const set of data.sets) this.setsByCode.set(set.code, set);
    for (const card of data.cards) {
      this.cardsById.set(card.id, card);
      const key = normaliseCardName(card.name);
      this.cardsByName.set(key, [...(this.cardsByName.get(key) ?? []), card]);
    }
    for (const printing of data.printings) {
      this.printingsById.set(printing.id, printing);
      this.printingsByCard.set(printing.cardId, [...(this.printingsByCard.get(printing.cardId) ?? []), printing]);
    }
  }

  /** Any catalogue card by id, variants included. */
  card(id: string): CatalogCard | undefined {
    return this.cardsById.get(id);
  }

  /** The rules identity of a card id: the base card for variant rows, the id itself otherwise. */
  resolveAlias(id: string): CardId {
    return this.cardsById.get(id)?.aliasOf ?? id;
  }

  /** The base (rules identity) card for any card id. */
  rulesCard(id: string): CatalogCard | undefined {
    return this.cardsById.get(this.resolveAlias(id));
  }

  /** Variant card ids that fold into `baseId`. */
  aliasesOf(baseId: string): CardId[] {
    return this.data.cards.filter((c) => c.aliasOf === baseId).map((c) => c.id);
  }

  /** Every card, variants included, in id order. */
  cards(): readonly CatalogCard[] {
    return this.data.cards;
  }

  /** Cards with this (normalised) name, variants included. */
  cardsNamed(name: string): readonly CatalogCard[] {
    return this.cardsByName.get(normaliseCardName(name)) ?? [];
  }

  printing(id: string): CatalogPrinting | undefined {
    return this.printingsById.get(id);
  }

  /** Printings of exactly this card id (not of its aliases). */
  printingsOf(cardId: string): readonly CatalogPrinting[] {
    return this.printingsByCard.get(cardId) ?? [];
  }

  /** The art for a deck card without a chosen printing (`public.plain_printing`). */
  plainPrinting(cardId: string): CatalogPrinting | undefined {
    const stored = this.cardsById.get(cardId)?.plainPrintingId;
    if (stored) return this.printingsById.get(stored);
    return plainestPrinting(this.printingsOf(cardId), this.data.sets);
  }

  set(code: string): CatalogSet | undefined {
    return this.setsByCode.get(code);
  }

  sets(): readonly CatalogSet[] {
    return this.data.sets;
  }

  /** `[Unique]` cards: one copy per deck (CR 825.3.a). */
  isUnique(card: CatalogCard): boolean {
    return isUniqueText(card.oracle, card.keywords);
  }
}

/** Builds a catalogue from snapshot data, validating it first (throws a zod error on malformed data). */
export function createCatalog(data: unknown): Catalog {
  return new Catalog(CatalogDataSchema.parse(data));
}

/** Builds a catalogue from data already known to be valid (skips validation; for trusted, pinned data). */
export function catalogFromTrustedData(data: CatalogData): Catalog {
  return new Catalog(data);
}
